import { useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import * as HomeStore from '../../stores/HomeStore'
import type { MonthlyDay } from '../../stores/HomeStore'

const BJ_OFFSET_MS = 8 * 60 * 60 * 1000
const DAY_HEADER = ['一', '二', '三', '四', '五', '六', '日']

function getCurrentMonthKey(): string {
  const bj = new Date(Date.now() + BJ_OFFSET_MS)
  return `${bj.getUTCFullYear()}-${String(bj.getUTCMonth() + 1).padStart(2, '0')}`
}

function shiftMonth(monthKey: string, delta: number): string {
  const [y, m] = monthKey.split('-').map(Number)
  const total = (y * 12 + (m - 1)) + delta
  const ny = Math.floor(total / 12)
  const nm = (total % 12 + 12) % 12
  return `${ny}-${String(nm + 1).padStart(2, '0')}`
}

interface SessionSummary { totalSets?: number; totalReps?: number; weight?: number; [k: string]: unknown }

function formatSessionMetric(
  durationSeconds: number,
  summary: SessionSummary | null,
  template: MonthlyDay['sessions'][number]['template'],
): string {
  const s = summary ?? {}
  if (template === 'timer' || template === 'laps_timer') return `${Math.round(durationSeconds / 60)}分钟`
  if (template === 'countdown') {
    const mins = Math.round(durationSeconds / 60)
    if (s.totalSets && mins > 0) return `${s.totalSets}组 ${mins}分钟`
    if (s.totalSets) return `${s.totalSets}组`
    return `${mins}分钟`
  }
  if (template === 'manual_reps') {
    const segs: string[] = []
    if (s.totalSets) segs.push(`${s.totalSets}组`)
    if (s.totalReps) segs.push(`${s.totalReps}次`)
    return segs.length > 0 ? segs.join(' ') : '—'
  }
  const mins = Math.round(durationSeconds / 60)
  return mins > 0 ? `${mins}分钟` : `${durationSeconds}秒`
}

function MonthlyCalendar() {
  const navigate = useNavigate()
  const [monthKey, setMonthKey] = useState<string>(() => getCurrentMonthKey())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const monthlyAtom = useMemo(() => HomeStore.monthlyStatsAtomFamily(monthKey), [monthKey])
  const { data } = useAtomValue(monthlyAtom)

  const monthLabel = data?.monthLabel ?? ''
  const cells = data?.cells ?? []
  const stats = data?.stats ?? { trainedDays: 0, aerobicMinutes: 0, anaerobicSets: 0, anaerobicReps: 0 }

  const goPrev = () => { setSelectedDate(null); setMonthKey((k) => shiftMonth(k, -1)) }
  const goNext = () => { setSelectedDate(null); setMonthKey((k) => shiftMonth(k, 1)) }

  const selectedCell = selectedDate ? cells.find(c => c.dateStr === selectedDate) : null

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      {/* Header */}
      <div className="flex-none pt-12 pb-2 px-6 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#1C1F2E' }}
        >
          <Icon icon="lucide:arrow-left" className="w-5 h-5 text-gray-300" />
        </button>
        <h1 className="text-xl font-bold text-white flex-1 text-center pr-10">月度活动</h1>
      </div>

      {/* Month switcher */}
      <div className="flex-none px-6 pt-3 pb-4 flex items-center gap-3">
        <button
          onClick={goPrev}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#1C1F2E' }}
        >
          <Icon icon="lucide:chevron-left" className="w-5 h-5 text-gray-300" />
        </button>
        <h2 className="flex-1 text-center text-lg font-bold text-white">{monthLabel}</h2>
        <button
          onClick={goNext}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#1C1F2E' }}
        >
          <Icon icon="lucide:chevron-right" className="w-5 h-5 text-gray-300" />
        </button>
      </div>

      <main className="flex-1 overflow-y-auto px-6 pb-10 flex flex-col gap-5">
        {/* Calendar grid */}
        <section>
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_HEADER.map((d) => (
              <div key={d} className="text-center text-xs text-gray-500 font-medium py-1">{d}</div>
            ))}
          </div>
          {/* Cells */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, i) => {
              if (cell.isPadding) {
                return <div key={i} className="aspect-square" />
              }
              const isSelected = selectedDate === cell.dateStr
              const hasTraining = cell.sessions.length > 0
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate(isSelected ? null : cell.dateStr)}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-1 transition-all
                    ${isSelected ? 'ring-2 ring-emerald-400' : ''}
                    ${cell.isToday ? 'border border-emerald-400' : ''}
                  `}
                  style={{
                    backgroundColor: hasTraining ? '#1C1F2E' : '#161826',
                  }}
                >
                  <span className={`text-sm font-bold ${cell.isFuture ? 'text-gray-600' : 'text-gray-200'}`}>
                    {cell.day}
                  </span>
                  <div className="flex gap-0.5 h-1.5">
                    {cell.hasAerobic && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                    {cell.hasAnaerobic && <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />}
                  </div>
                </button>
              )
            })}
          </div>
        </section>

        {/* Monthly stats */}
        <section
          className="rounded-2xl px-5 py-4 flex flex-col gap-2"
          style={{ backgroundColor: '#1C1F2E', borderBottom: '4px solid #151722' }}
        >
          <h3 className="text-sm font-bold text-gray-400">本月统计</h3>
          <div className="text-sm text-gray-300 leading-relaxed">
            训练 <span className="text-white font-bold">{stats.trainedDays}</span> 天
            <span className="text-gray-500 mx-2">·</span>
            <span className="text-emerald-400">有氧</span>{' '}
            <span className="text-white font-bold">{stats.aerobicMinutes}</span> 分钟
            <span className="text-gray-500 mx-2">·</span>
            <span className="text-violet-400">无氧</span>{' '}
            <span className="text-white font-bold">{stats.anaerobicSets}</span> 组
            {stats.anaerobicReps > 0 && (
              <>
                <span className="text-gray-500 mx-2">/</span>
                <span className="text-white font-bold">{stats.anaerobicReps}</span> 次
              </>
            )}
          </div>
        </section>

        {/* Selected day detail */}
        {selectedCell && (
          <section className="flex flex-col gap-2">
            <div className="flex items-center gap-2 px-1">
              <Icon icon="lucide:calendar-days" className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-bold text-gray-300">
                {selectedCell.dateStr.slice(5).replace('-', '/')}
                {selectedCell.isToday && <span className="text-emerald-400 ml-1">（今天）</span>}
              </h3>
            </div>
            <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: '#1C1F2E' }}>
              {selectedCell.sessions.length > 0 ? (
                selectedCell.sessions.map((s, j) => (
                  <div
                    key={j}
                    className={`flex items-center gap-3 py-2 ${j < selectedCell.sessions.length - 1 ? 'border-b border-[#2A2D3A]' : ''}`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-none"
                      style={{ backgroundColor: s.exerciseType === 'aerobic' ? '#10B981' : '#8B5CF6' }}
                    />
                    <span className="text-sm text-white font-medium flex-1 truncate">
                      {s.label}
                      {(() => {
                        const w = (s.summary as SessionSummary | null)?.weight
                        return typeof w === 'number' && w > 0 ? `-${w}kg` : ''
                      })()}
                    </span>
                    <span className="text-sm text-white font-bold">
                      {formatSessionMetric(s.durationSeconds, s.summary as SessionSummary | null, s.template)}
                    </span>
                    <span className="text-xs text-gray-500 font-mono w-12 text-right">{s.startTime}</span>
                  </div>
                ))
              ) : (
                <div className="flex items-center gap-2 py-2">
                  <Icon icon="lucide:coffee" className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">休息日</span>
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}

export default MonthlyCalendar
